import { Button } from '@/global/components/Button'
import CircularProgress from '@/global/components/CircularProgress'
import { hasPermission } from '@/hooks/useBackendMenu'
import { CategoriaEnum } from '@acme/auth'
import { OriginProps, parserRegisterOrigin } from '@acme/common'
import { SN, StatusAprovacao } from '@acme/core'
import Dialog from 'global/guides/Dialog'
import ConfirmarExclusao from 'global/guides/Dialog/ConfirmarExclusao'
import { useAuth } from 'hooks'
import { PrinterIcon } from 'lucide-react'
import dayjs from 'dayjs'
import { useCallback, useContext, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { ErrorHandler, Registro, registros as registrosApi } from 'service/api'
import { toast } from 'sonner'
import EntradaDiaSeguinte from '../atoms/entrada-dia-seguinte'
import SaidaDiaAnterior from '../atoms/saida-dia-anterior'
import StatusOcorrencia from '../atoms/StatusOcorrencia'
import { NotasAdesivasDetalhesRegistro } from '../components/notas-adesivas/notas-adesivas-detalhes-registro'
import { FormularioDetalhesRegistro } from '../forms/FormDetalhesRegistro'
import { DigitacaoContext } from '../pages/useDigitacaoContext'
import { useUpdateDigitacaoQueryData } from '../pages/useDigitacaoData'
import useService from '../service'

type FnHandle = {
    (data: { id: number; motivo: string }): void
    (data: { id: number }): void
}

type ItemContexto = Registro | { registros?: Registro[] }

/**
 * Reúne os registros das duas fontes que montam este modal: a digitação serve
 * jornadas (`{ registros: [...] }`) pelo `DigitacaoContext`, e as telas de
 * leitura — registros excluídos, fora da cerca e registros do cliente — servem
 * registros diretos pelo outlet context.
 *
 * `useContext` direto em vez de `useDigitacaoContext()` porque o hook loga erro
 * quando não há provider, e nas telas de leitura não há mesmo.
 */
function useRegistrosDoContexto(): Registro[] {
    const digitacao = useContext(DigitacaoContext)
    const outlet = useOutletContext<{ data?: ItemContexto[] }>()

    const itens = (digitacao?.data ?? outlet?.data ?? []) as ItemContexto[]

    return itens.flatMap(item =>
        item && 'registros' in item
            ? (item.registros ?? [])
            : [item as Registro],
    )
}

interface Props {
    editable?: boolean
}

export function DetalhesRegistro({ editable = false }: Props) {
    const { decoded } = useAuth()
    const { handleAprovar, handleReprovar, handleExcluir } = useService()
    const { registroId } = useParams()
    const registros = useRegistrosDoContexto()
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const registro = registros.find(
        registro => registro?.id === Number(registroId),
    )
    const digitacao = useContext(DigitacaoContext)
    // Nas telas de leitura não há contexto da digitação, e o título fica só com data e hora.
    const referencia = registro
        ? [
              digitacao?.getFuncionarioNome(registro.funcionarioId),
              `${dayjs.utc(registro.data).format('DD/MM/YYYY')} ${registro.hora}`,
          ]
              .filter(Boolean)
              .join(' · ')
        : ''
    const { title, exibirComprovante } = parserRegisterOrigin(
        registro?.origem!,
    ) as OriginProps
    const [isImprimindo, setIsImprimindo] = useState(false)
    const [excluindo, setExcluindo] = useState(false)
    const [isTogglingVirada, setTogglingVirada] = useState(false)
    const [saidaDiaAnterior, setSaidaDiaAnterior] = useState(
        registro?.saidaDiaAnterior,
    )
    const [entradaDiaSeguinte, setEntradaDiaSeguinte] = useState(
        registro?.entradaDiaSeguinte,
    )

    const handleClickComprovante = async () => {
        setIsImprimindo(true)
        registrosApi
            .pdf({
                queryParam: { id: registro?.id },
            })
            .finally(() => setIsImprimindo(false))
    }

    const handleClickExcluir: FnHandle = data => {
        handleExcluir('REGISTRO', {
            ...registro,
            ...data,
        })
    }

    const toggle = useCallback(
        async (tipo: 'entrada-dia-seguinte' | 'saida-dia-anterior') => {
            const value =
                tipo === 'entrada-dia-seguinte'
                    ? entradaDiaSeguinte
                    : saidaDiaAnterior

            const toggle = value === 'S' ? 'N' : 'S'

            const msgAtualizar =
                tipo === 'entrada-dia-seguinte'
                    ? 'Entrada no dia seguinte atualizada'
                    : 'Saída no dia anterior atualizada'

            const msgRemover =
                tipo === 'entrada-dia-seguinte'
                    ? 'Entrada no dia seguinte removida'
                    : 'Saída no dia anterior removida'

            try {
                setTogglingVirada(true)

                const registro = await registrosApi.patchFrom(
                    registroId!,
                    tipo,
                    {},
                )

                if (tipo === 'entrada-dia-seguinte') {
                    setEntradaDiaSeguinte(toggle)
                } else {
                    setSaidaDiaAnterior(toggle)
                }

                setQueryData('REGISTRO', registro)

                if (toggle === 'S') {
                    toast.success(msgAtualizar)
                    return
                } else {
                    toast.success(msgRemover)
                }
            } catch (error) {
                const { message } = error as ErrorHandler
                toast.error(message)
            } finally {
                setTogglingVirada(false)
            }
        },
        [saidaDiaAnterior, entradaDiaSeguinte],
    )

    const funcionarioPodeAjustar = funcionarioPodeSolicitarAjustes()

    if (!registro) return null

    const podeAtualizar = editable && hasPermission(['update', 'Registros'])
    const podeExcluir =
        editable &&
        (hasPermission(['delete', 'Registros']) || funcionarioPodeAjustar)

    return (
        <Dialog.Root
            defaultValues={{
                ...registro,
                origem: title,
                endereco: registro?.Local?.endereco,
            }}
        >
            <Dialog.Title
                action="Detalhes do registro"
                endAdornment={
                    <div className="flex items-center gap-2">
                        <NotasAdesivasDetalhesRegistro registro={registro} />
                        {!registro?.deletedAt && exibirComprovante && (
                            <Button
                                variant="outlined"
                                startIcon={
                                    isImprimindo ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <PrinterIcon />
                                    )
                                }
                                onClick={handleClickComprovante}
                            >
                                comprovante
                            </Button>
                        )}
                    </div>
                }
            >
                {referencia}
            </Dialog.Title>
            <Dialog.Content>
                <FormularioDetalhesRegistro />
                {editable && (
                    <Dialog.Content.Footer>
                        <StatusOcorrencia
                            tipoOcorrencia="REGISTRO"
                            onApprove={
                                podeAtualizar
                                    ? () => handleAprovar('REGISTRO', registro)
                                    : undefined
                            }
                            onReject={
                                podeAtualizar
                                    ? ({ motivo }) =>
                                          handleReprovar('REGISTRO', {
                                              id: registro.id,
                                              motivo,
                                          })
                                    : undefined
                            }
                        />
                        {podeAtualizar && (
                            <div className="flex flex-row flex-wrap items-center gap-x-4 justify-between pb-8">
                                <SaidaDiaAnterior
                                    categoria={decoded?.categoria!}
                                    entradaDiaSeguinte={entradaDiaSeguinte}
                                    saidaDiaAnterior={saidaDiaAnterior}
                                    isLoading={isTogglingVirada}
                                    toggle={toggle}
                                />
                                <EntradaDiaSeguinte
                                    categoria={decoded?.categoria!}
                                    entradaDiaSeguinte={entradaDiaSeguinte}
                                    saidaDiaAnterior={saidaDiaAnterior}
                                    isLoading={isTogglingVirada}
                                    toggle={toggle}
                                />
                            </div>
                        )}
                    </Dialog.Content.Footer>
                )}
                <ConfirmarExclusao
                    width="2xl"
                    open={excluindo}
                    onClose={() => setExcluindo(false)}
                    exigirMotivo={
                        registro.statusAprovacao === StatusAprovacao.APROVADO
                    }
                    onConfirm={({ motivo }) =>
                        handleClickExcluir({ id: registro.id, motivo: motivo! })
                    }
                    title="Tem certeza que deseja excluir o registro?"
                />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                {podeExcluir && (
                    <Dialog.Actions.PrimaryButton
                        color="error"
                        data-testid="dialog-delete-button"
                        onClick={() => setExcluindo(true)}
                    >
                        Excluir
                    </Dialog.Actions.PrimaryButton>
                )}
                <Dialog.Actions.PrimaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}

export function funcionarioPodeSolicitarAjustes() {
    const { decoded, funcionarioSelecionado } = useAuth()
    return (
        decoded.categoria === CategoriaEnum.Funcionario &&
        SN.isTrue(funcionarioSelecionado?.ajustesPonto)
    )
}
